import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './topUp.css'
import coinIcon from '../assets/icon/coinIcon.png'
import { createSubscriptionCheckout, createTokenCheckout, createPaymentIntent } from '../functions/topup'
import { updateDcoin, getUserInfo, getRechargeHistory, RechargeRecord } from '../functions';
import { getToken, getUsername } from '../utils/auth';
import StripePaymentModal from './StripePaymentModal';
import { savePendingTransaction, removePendingTransaction, getPendingTransactions } from '../utils/transactionRecovery';
import { useLanguage } from '../contexts/LanguageContext';

interface MembershipPlan {
    id: string;
    name: string;
    monthlyPrice: number;
    annualPrice: number;
    discount: number;
    discountPercent: string;
    benefits: string[];
    recommended?: boolean;
    colorTheme?: string;
}

const TopUp = () => {
    // ... existing hooks
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState<'token' | 'history'>('token');
    // ... existing states
    const [planSubscriptionTypes, setPlanSubscriptionTypes] = useState<Record<string, 'monthly' | 'annual'>>({
        basic: 'monthly',
        master: 'monthly',
        grandmaster: 'monthly',
        legendary: 'annual'
    });
    const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
    const [purchasingPackageId, setPurchasingPackageId] = useState<string | null>(null);

    // User's current coin balance
    const [userCoin, setUserCoin] = useState<number>(0);
    const [loadingBalance, setLoadingBalance] = useState(true);

    // Stripe Modal State
    const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [pendingItemType, setPendingItemType] = useState<'subscription' | 'token' | null>(null);
    const [pendingItemId, setPendingItemId] = useState<string | null>(null);

    // Top-up history state
    const [topUpHistory, setTopUpHistory] = useState<Array<{ id: string; amount: number; status: string; timestamp: string, currency: any }>>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Fetch user balance on mount
    useEffect(() => {
        const fetchUserBalance = async () => {
            try {
                const token = getToken();
                if (token) {
                    const userInfo = await getUserInfo(token);
                    if (userInfo && userInfo.coin !== undefined) {
                        setUserCoin(userInfo.coin);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch user balance:', error);
            } finally {
                setLoadingBalance(false);
            }
        };
        fetchUserBalance();
    }, []);

    // Auto-retry pending transactions on mount
    useEffect(() => {
        const retryFailedTransactions = async () => {
            const pending = getPendingTransactions();
            if (pending.length === 0) return;

            const token = getToken();
            if (!token) return;

            // console.log('Found pending transactions:', pending);

            try {
                const userInfo = await getUserInfo(token);
                if (userInfo && userInfo.username) {
                    for (const tx of pending) {
                        if (tx.itemType === 'token' && tx.dcoinAmount) {
                            try {
                                await updateDcoin(token, userInfo.username, tx.dcoinAmount);
                                // console.log(`Recovered transaction ${tx.paymentId}`);
                                removePendingTransaction(tx.paymentId);
                                alert(t.topUpRecovered(tx.dcoinAmount));
                            } catch (err) {
                                console.error(`Failed to recover transaction ${tx.paymentId}`, err);
                            }
                        }
                    }
                }
            } catch (e) {
                console.error("Could not fetch user info for recovery", e);
            }
        };

        retryFailedTransactions();
    }, []);

    // ... existing mock data

    // ... existing handlers

    const handlePaymentSuccess = async (paymentId: string) => {
        setIsStripeModalOpen(false);

        if (pendingItemType === 'token' && pendingItemId) {
            const pkg = premiumPackages.find(p => p.id === pendingItemId);
            if (pkg) {
                // 1. Save receipt first (Safety Net)
                savePendingTransaction({
                    paymentId,
                    itemType: 'token',
                    itemId: pendingItemId,
                    dcoinAmount: pkg.dcoin,
                    timestamp: Date.now()
                });

                const token = getToken();
                if (token) {
                    try {
                        const userInfo = await getUserInfo(token);
                        if (userInfo && userInfo.username) {
                            await updateDcoin(token, userInfo.username, pkg.dcoin);

                            // 2. Success! Remove receipt
                            removePendingTransaction(paymentId);

                            alert(t.topUpPaymentSuccess(pkg.dcoin));

                            // 3. Force page reload to ensure fresh state
                            window.location.reload();
                        } else {
                            throw new Error('Could not retrieve valid username.');
                        }
                    } catch (err: any) {
                        console.error(err);
                        // 3. Failed! Update failed, but receipt is saved.
                        alert(t.topUpPaymentNetworkError);
                    }
                } else {
                    alert(t.topUpPaymentNoUser);
                }
            }
        } else if (pendingItemType === 'subscription' && pendingItemId) {
            alert(t.topUpSubscriptionActivated(pendingItemId));
            // Force page reload to ensure fresh state
            window.location.reload();
        }

        setPendingItemId(null);
        setPendingItemType(null);
    };

    // Mock current plan data
    const currentPlan = {
        name: 'GRANDMASTER MEMBER',
        expiryDate: '2024-12-31',
        dCoin: 234,
        progress: 20,
        discount: '20% off'
    };

    // Premium packages for token top-up
    const premiumPackages = [
        {
            id: '1',
            dcoin: 100,
            baseDcoin: 100,
            packageBonus: 0,
            planBonus: 10,
            price: 1
        },
        {
            id: '2',
            dcoin: 1100,
            baseDcoin: 1000,
            packageBonus: 100,
            planBonus: 110,
            price: 10
        },
        {
            id: '3',
            dcoin: 5500,
            baseDcoin: 5000,
            packageBonus: 500,
            planBonus: 550,
            price: 50
        },
        {
            id: '4',
            dcoin: 11500,
            baseDcoin: 10000,
            packageBonus: 1500,
            planBonus: 1150,
            price: 100
        }
    ];

    // Fetch top-up history from API
    useEffect(() => {
        const fetchTopUpHistory = async () => {
            if (activeTab !== 'history') return;

            const token = getToken();
            const uid = getUsername();
            if (!token || !uid) return;

            setHistoryLoading(true);
            try {
                const result = await getRechargeHistory(token, uid);
                if (result.success && result.data.records) {
                    const formattedHistory = result.data.records.map((record: RechargeRecord) => ({
                        id: record.id,
                        amount: record.amount,
                        status: record.status === 'successful' ? 'Successful' :
                            record.status === 'failed' ? 'Failed' : 'Pending',
                        timestamp: formatTimestamp(record.timestamp),
                        currency: record.currency
                    }));
                    setTopUpHistory(formattedHistory);
                }
            } catch (error) {
                console.error('Failed to fetch top-up history:', error);
            } finally {
                setHistoryLoading(false);
            }
        };
        fetchTopUpHistory();
    }, [activeTab]);

    // Format timestamp helper
    const formatTimestamp = (timestamp: string): string => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return timestamp;
        }
    };

    const membershipPlans: MembershipPlan[] = [
        {
            id: 'basic',
            name: 'Basic Member',
            monthlyPrice: 0.00,
            annualPrice: 0.00,
            discount: 100,
            discountPercent: 'Save 100%',
            benefits: [
                'Standard Reading',
                'AI-Driven Divination',
                'Integrate Ancient Wisdom',
                'Multiple Divination Methods',
                'Vast Collection of Card Spreads',
                'Vast Interpretation Materials',
                '20 DCoin/Day (Max 600/Month)',
                'A maximum of 1 follow-up inquiries'
            ],
        },
        {
            id: 'master',
            name: 'Master Member',
            monthlyPrice: 19.99,
            annualPrice: 169.99,
            discount: 29.1,
            discountPercent: 'Save 29.1%',
            benefits: [
                'All in Basic',
                '2000 DCoins Per Month',
                '50 DCoin/Day (Max 1,500/Month)',
                'Top-Up Bonus: +10% DCoin',
                'Deep Reading',
                'Max 5 Follow-Ups',
                'Exclusive member badge'
            ]
        },
        {
            id: 'grandmaster',
            name: 'Grandmaster Member',
            monthlyPrice: 49.99,
            annualPrice: 399.99,
            discount: 33.3,
            discountPercent: 'Save 33.3%',
            benefits: [
                'All in Master',
                '5000 DCoins Per Month',
                '100 DCoin/Day (Max 3,000/Month)',
                'Top-Up Bonus: +20% DCoin',
                'Max 10 Follow-Ups',
                'Advanced Model',
                'Fast Response'
            ],
            recommended: true
        },
        {
            id: 'legendary',
            name: 'Legendary Member',
            monthlyPrice: 99.99,
            annualPrice: 699.99,
            discount: 41.6,
            discountPercent: 'Save 41.6%',
            benefits: [
                'All in Grandmaster',
                '10000 DCoins Per Month',
                '200 DCoin/Day (Max 6,000/Month)',
                'Top-Up Bonus: +30% DCoin',
                'Max 20 Follow-Ups',
                'Top-Tier Model',
                'Highest-Priority Response'
            ],
            colorTheme: 'gold'
        }
    ];

    const handleSubscription = async (planId: string) => {
        if (subscribingPlan) return;
        const billingCycle = planSubscriptionTypes[planId] || 'monthly';
        const plan = membershipPlans.find(p => p.id === planId);
        if (!plan) return;

        const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;

        if (price <= 0) {
            alert(t.topUpFreePlan);
            return;
        }

        setPendingItemType('subscription');
        setPendingItemId(planId);
        setPaymentAmount(price);
        setIsStripeModalOpen(true);
    };

    const handleTokenPurchase = async (pkgId: string, price: number, dcoin: number) => {
        if (purchasingPackageId) return;

        setPendingItemType('token');
        setPendingItemId(pkgId);
        setPaymentAmount(price);
        setIsStripeModalOpen(true);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className="topup-page">
            <StripePaymentModal
                isOpen={isStripeModalOpen}
                onClose={() => setIsStripeModalOpen(false)}
                amount={paymentAmount}
                onSuccess={handlePaymentSuccess}
            />

            {/* Back Button */}
            <button className="topup-back-btn" onClick={handleGoBack} aria-label={t.topUpBack}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Header Section */}
            <header className="topup-header">
                <div className="topup-header-content">
                    <h1 className="topup-title">{t.topUpTitle}</h1>
                    <p className="topup-subtitle">{t.topUpSubtitle}</p>
                </div>

                {/* Tabs */}
                <div className="topup-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'token' ? 'active' : ''}`}
                        onClick={() => setActiveTab('token')}
                    >
                        {t.topUpTokenTab}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        {t.topUpHistoryTab}
                    </button>
                </div>
            </header>

            {/* Current Balance Section - Show for token tabs */}
            {(activeTab === 'token') && (
                <section className="current-plan-section">
                    <div className="current-plan-card current-plan-card-horizontal">
                        <div className="current-plan-left">
                            <svg className="plan-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                                <text x="12" y="16" textAnchor="middle" fontSize="12" fill="currentColor">D</text>
                            </svg>
                            <span className="current-plan-name">{t.topUpCurrentBalance}</span>
                        </div>
                        <div className="current-plan-right">
                            <div className="plan-dcoin">
                                {loadingBalance ? t.topUpLoading : userCoin}
                                <span>DCOIN</span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Main Content */}
            <main className="topup-main">
                {activeTab === 'token' && (
                    <div className="token-recharge">
                        <div className="premium-packages-grid">
                            {premiumPackages.map((pkg) => (
                                <div key={pkg.id} className="premium-package-card">
                                    <div className="package-top-block">
                                        <div className="package-label">{t.topUpPremiumPackage}</div>
                                        <div className="package-dcoin-container">
                                            <img src={coinIcon} alt="Dcoin" className="dcoin-icon" />
                                            <div className="package-dcoin-amount">
                                                {pkg.dcoin}
                                                <span className="package-dcoin-label">Dcoin</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="package-bottom-block">
                                        <ul className="package-benefits">
                                            <li className="package-benefit-item">
                                                <span className="benefit-plus">+</span>
                                                <span>{pkg.baseDcoin} Dcoin</span>
                                                <svg className="benefit-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 6L9 17l-5-5" />
                                                </svg>
                                            </li>
                                            {pkg.packageBonus > 0 && (
                                                <li className="package-benefit-item">
                                                    <span className="benefit-plus">+</span>
                                                    <span>{pkg.packageBonus} {t.topUpBonusFromPackage}</span>
                                                    <svg className="benefit-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M20 6L9 17l-5-5" />
                                                    </svg>
                                                </li>
                                            )}

                                        </ul>
                                        <button
                                            className="package-buy-btn"
                                            onClick={() => handleTokenPurchase(pkg.id, pkg.price, pkg.dcoin)}
                                            disabled={purchasingPackageId === pkg.id}
                                        >
                                            {purchasingPackageId === pkg.id ? t.topUpProcessing : `$${pkg.price} ${t.topUpBuyNow}`}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="recharge-history">
                        <div className="history-list">
                            {topUpHistory.map((item) => (
                                <div key={item.id} className="history-item">
                                    <div className="history-amount-status">
                                        {item.amount} - {item.currency} - {item.status}
                                    </div>
                                    <div className="history-timestamp">
                                        {item.timestamp}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}

export default TopUp
